import { Card } from '@/components/ui/Card';
import { PnlDisplay } from '@/components/ui/PnlDisplay';
import { usePerformanceSummary } from '@/hooks/queries/useAnalytics';
import { formatCurrency } from '@/lib/formatters';

export function PerformanceSummary() {
  const { data: summary, isLoading } = usePerformanceSummary();

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
            <div className="h-8 bg-gray-200 rounded w-3/4" />
          </Card>
        ))}
      </div>
    );
  }

  if (!summary) return null;

  const stats = [
    {
      label: 'Realized P&L',
      value: <PnlDisplay value={summary.totalRealizedPnl} size="lg" />,
    },
    {
      label: 'Win Rate',
      value: (
        <span className="text-lg font-semibold text-gray-900">
          {summary.winRate != null ? `${summary.winRate.toFixed(1)}%` : '---'}
        </span>
      ),
    },
    {
      label: 'Profit Factor',
      value: (
        <span className="text-lg font-semibold text-gray-900">
          {summary.profitFactor == null || summary.profitFactor === Infinity ? '---' : summary.profitFactor.toFixed(2)}
        </span>
      ),
    },
    {
      label: 'Net Premium',
      value: (() => {
        const net = summary.totalPremiumCollected - summary.totalPremiumPaid;
        return (
          <span className={`text-lg font-semibold ${net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(net)}
          </span>
        );
      })(),
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {stats.map((stat) => (
        <Card key={stat.label}>
          <p className="text-sm text-gray-500">{stat.label}</p>
          <div className="mt-1">{stat.value}</div>
        </Card>
      ))}
    </div>
  );
}

export function PerformanceCards() {
  const { data: summary, isLoading } = usePerformanceSummary();

  if (isLoading || !summary) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Average Win</p>
            <p className="text-lg font-semibold text-green-600">
              {formatCurrency(summary.averageWin)}
            </p>
          </div>
          <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Average Loss</p>
            <p className="text-lg font-semibold text-red-600">
              {formatCurrency(summary.averageLoss)}
            </p>
          </div>
          <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Premium Paid</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatCurrency(summary.totalPremiumPaid)}
            </p>
          </div>
          <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
            <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
        </div>
      </Card>
    </div>
  );
}
